// The non-cast verb: opening, reading, pulling, placing.
//
// Everything else the player can do has lived in `sim/` since the Phase 0
// extraction. This did not, and the gap was invisible until something tried to
// enumerate what a player can do: the Puzzle Analysis search (2026-08-13) could
// not solve room1, whose entire puzzle is *open the crate, take the Rusty Key,
// unlock the door*, because no command could express opening a crate. Nor could
// a `Recording` contain one, so no take of any room with an interaction in it
// could ever be recorded.
//
// **What moved here is the decision; what stayed in `game/src/ui.ts` is the
// prose.** `resolveInteractText` did both in one pass -- granting the item and
// composing the sentence about having granted it, interleaved with analytics
// calls and tutorial triggers. That is the same interleaving `sim/events.ts`
// was created to end ("a mechanism function both mutated world state *and*
// reached for a Phaser sprite in the same breath"), arrived at from the other
// direction. This returns a tagged outcome; the game turns it into a line.
//
// The state itself was already here: `inventory`, `collectedItemIds`,
// `learnedRunes` and `doorLocks` are all `WorldState` fields, and `game/`'s
// `inventory.ts` / `learnedRunes.ts` / `doors.ts` have been thin accessors over
// them since Phase 0 step 2. So this is not moving data, only the four
// if-branches that decide which field to write.
import { ObjectDef, RoomDef } from './types';
import { WorldState } from './state';
import { SimEvents } from './events';
import { settleWorld } from './settle';
import { isConsumedOnUse } from './items';
import { throwBreaker } from './breaker';
import { isConveyorConsole, reverseConveyor } from './conveyor';

/** What an interaction did, tagged so a caller can narrate it.
 *
 * Mirrors `CastOutcome`'s shape and exists for the same reason: the sim reports
 * what happened and the renderer owns how it reads. `already` distinguishes a
 * repeat visit from a first one, which is the whole of why several of these
 * branches exist -- a crate that has already been opened must not hand out a
 * second key. */
export type InteractOutcome =
  /** No `interact` at all, or one that only carries text. */
  | { kind: 'none' }
  | { kind: 'item'; itemId: string; already: boolean }
  | { kind: 'rune'; runeId: string; already: boolean }
  /** An item consumed into a slot. `missing` when the player is not carrying
   * it, which is the ordinary case for an altar they have not found the crystal
   * for yet. */
  | { kind: 'placed'; itemId: string; missing: boolean }
  | { kind: 'unlocked'; lockId: string; already: boolean }
  /** A Circuit Breaker thrown by hand. `open` is the position it landed in, and
   * there is no `already` here on purpose: a switch has no such state, it just
   * goes the other way. */
  | { kind: 'breaker'; open: boolean }
  /** A Conveyor Console's lever thrown. `belts` is how many segments actually
   * reversed -- 0 for a console standing against nothing, which is the one way
   * this can happen and change nothing. No `already` here either, for the same
   * reason a breaker has none: it went the other way, and the other way is a
   * different machine. */
  | { kind: 'conveyor'; belts: number };

/** What using a key on a locked doorway did. */
export type UnlockOutcome =
  /** Nothing there, or nothing locked. */
  | { kind: 'none' }
  /** Locked, and this door has no key at all -- something else in the room
   * opens it (an `interact.unlocksDoorId` lever). */
  | { kind: 'no-key'; lockId: string }
  /** Locked by a key the caster is not carrying. */
  | { kind: 'missing'; lockId: string; keyItemId: string }
  | { kind: 'unlocked'; lockId: string; keyItemId: string; consumed: boolean };

/** Using whatever key the caster carries on the doorway at a tile.
 *
 * The last piece of the interaction gap (2026-08-13). `interact.unlocksDoorId`
 * -- a lever -- became a command with `applyInteract` above; this is the other
 * way a door opens, and it is a genuinely different shape: there is no object
 * to interact with, only a doorway, and what decides the outcome is what the
 * caster is carrying.
 *
 * In the game this is reached by *walking into* a locked doorway, which opens a
 * panel with a "Use <key>" button (`ui.ts`'s door panel). That is why it takes a
 * tile rather than an id: the doorway is the thing, and `DoorDef` has no id.
 *
 * Consuming the key is gated on `isConsumedOnUse`, which is why the item
 * catalogue had to move into `sim/` before this could be written honestly --
 * a one-use key crumbles in the lock and an ordinary one is kept, and that fact
 * lived above the simulation until today. */
export function applyUnlock(world: WorldState, x: number, y: number): UnlockOutcome {
  const room = world.rooms[world.player.roomId];
  const door = room?.doors.find((d) => d.x === x && d.y === y);
  if (!door?.lockId) return { kind: 'none' };
  // `doorLocks` stores *is locked*; `false` is open. See `goal.ts`'s
  // `isDoorUnlocked` for why that polarity gets a predicate of its own.
  if (world.doorLocks[door.lockId] === false) return { kind: 'none' };
  if (!door.keyItemId) return { kind: 'no-key', lockId: door.lockId };
  if (!world.inventory.has(door.keyItemId)) return { kind: 'missing', lockId: door.lockId, keyItemId: door.keyItemId };

  world.doorLocks[door.lockId] = false;
  const consumed = isConsumedOnUse(door.keyItemId);
  if (consumed) world.inventory.delete(door.keyItemId);
  return { kind: 'unlocked', lockId: door.lockId, keyItemId: door.keyItemId, consumed };
}

/** Applies whatever the object's `interact` does to the world.
 *
 * **First match wins**, in the order `giveItemId`, `giveRuneId`, `placeItemId`,
 * `unlocksDoorId` -- preserved exactly from `resolveInteractText`, where each
 * branch returned. Nothing authored carries two of them, so the order is a
 * tie-break that has never been needed; it is kept identical anyway, because
 * "nothing uses it" is a fact about today's content rather than a rule.
 *
 * Returns `{kind:'none'}` for a flavour-only interact, which is not a failure:
 * most `interact` blocks in the world are a paragraph on a signpost.
 */
export function applyInteract(world: WorldState, objectId: string, out: SimEvents): InteractOutcome {
  const room: RoomDef | undefined = world.rooms[world.player.roomId];
  const object: ObjectDef | undefined = room?.objects.find((o) => o.id === objectId);
  const interact = object?.interact;
  if (!room || !object) return { kind: 'none' };

  // First, and one of the two branches that do not read `interact` at all:
  // carrying a
  // `breaker` cluster is the whole signal that Interact throws it, the same way
  // carrying `vines` is the whole signal an object follows the vine ladder. An
  // `interact` block is prose about the switch, not the thing that makes it one,
  // so a breaker with no text still throws.
  //
  // Settles, and has to: `isConductive` reads the switch, so throwing one
  // rewires the room. Every reader on the line -- a gate terminal, a lamp, a
  // gate pillar's path, a live floor grid, the drawn shape of the cable running
  // into it -- is recomputed by the systems that call runs, in their declared
  // order. It is also what re-trips a breaker closed back onto an overload that
  // has not gone away, with no rule of its own for it.
  if (object.breaker) {
    const open = throwBreaker(object);
    settleWorld(world, out);
    return { kind: 'breaker', open };
  }

  // Second, and the same shape: carrying `conveyor.console` is the whole signal
  // that Interact throws the reversing lever. Settles because a belt's heading is
  // half its presentation -- `rustFamily` reads `conveyor.direction`, so the
  // settle is what repaints every arrow in the run, renames nothing and rewrites
  // each segment's description to say which way it now runs.
  if (isConveyorConsole(object)) {
    const belts = reverseConveyor(room, object, world.physics);
    if (belts > 0) settleWorld(world, out);
    return { kind: 'conveyor', belts };
  }

  if (!interact) return { kind: 'none' };

  if (interact.giveItemId) {
    const itemId = interact.giveItemId;
    // Gated on `collectedItemIds`, never on `inventory`: a one-use key is
    // removed from `inventory` the moment it unlocks its door, and `inventory`
    // alone cannot tell "already had this and used it" from "never had it".
    // A real, reported bug before that set existed -- see `WorldState`.
    const already = world.collectedItemIds.has(itemId);
    if (!already) {
      world.inventory.add(itemId);
      world.collectedItemIds.add(itemId);
    }
    return { kind: 'item', itemId, already };
  }

  if (interact.giveRuneId) {
    const runeId = interact.giveRuneId;
    const already = world.learnedRunes.has(runeId);
    if (!already) world.learnedRunes.add(runeId);
    return { kind: 'rune', runeId, already };
  }

  if (interact.placeItemId) {
    const itemId = interact.placeItemId;
    if (!world.inventory.has(itemId)) return { kind: 'placed', itemId, missing: true };
    world.inventory.delete(itemId);
    // Filling the altar is the point of placing, and it is done here rather
    // than announced for the scene to do -- which is what `triggerAltarItemPlaced`
    // was: an event the owning GameScene listened for in order to write
    // `altar.filled` back. That listener was the last piece of world state being
    // written from the renderer.
    if (object.altar) {
      object.altar.filled = true;
      if (object.altar.filledInteract) object.interact = object.altar.filledInteract;
      // The lit look is **written back as well as announced** (2026-08-13, its
      // own change, after the extraction deliberately preserved the old
      // behaviour first). `presentation.ts` states the rule this follows and
      // the reason: "sprites are rebuilt from `obj.texture` on every room
      // entry, so an event alone would forget the stage the moment the player
      // left and came back."
      //
      // A filled altar is exactly that case, and it is *not* the gate case.
      // A gate's open look survives announce-only because
      // `checkGateThreshold` restates it on every settle for any gate that can
      // be seen open; a conduit pillar's likewise, on every gate check. Nothing
      // restates an altar's. So before this, filling all four of room10's
      // pillars and stepping back through the door showed four unlit pillars
      // over an open Portal.
      if (object.litTexture) {
        object.texture = object.litTexture;
        out.push({ kind: 'texture-set', objectId: object.id, texture: object.texture, transition: 'pop' });
      }
      out.push({ kind: 'interact-changed', objectId: object.id });
      // `checkRuneAltars` is a settle system, and filling the last altar is
      // exactly what makes it fire -- so this has to settle rather than leave
      // the swap until whatever happens next.
      settleWorld(world, out);
    }
    return { kind: 'placed', itemId, missing: false };
  }

  if (interact.unlocksDoorId) {
    const lockId = interact.unlocksDoorId;
    // `doorLocks` stores *is locked*, so `false` is what opening one writes and
    // an absent entry is a locked door -- see `sim/goal.ts`'s `isDoorUnlocked`
    // for why that polarity gets its own predicate.
    const already = world.doorLocks[lockId] === false;
    if (!already) world.doorLocks[lockId] = false;
    return { kind: 'unlocked', lockId, already };
  }

  return { kind: 'none' };
}

/** Whether interacting with this object *could* change the world -- the line
 * between a lever and a plaque, read off the authored data. What a search uses
 * to decide an interaction is worth trying at all. */
export function interactDoesSomething(object: ObjectDef): boolean {
  const interact = object.interact;
  if (object.breaker) return true;
  // A console is a lever whatever it is standing next to: whether it reaches a
  // belt is a fact about the room, and answering "could this do something" by
  // walking the run would make a search's own move list depend on where the
  // boulders currently are. It is the same claim the breaker branch above makes
  // -- a switch is worth trying.
  if (isConveyorConsole(object)) return true;
  return Boolean(interact && (interact.giveItemId || interact.giveRuneId || interact.placeItemId || interact.unlocksDoorId));
}

/** Whether an interaction *did* change the world -- what decides the turn.
 *
 * Distinct from `interactDoesSomething` above, and the difference is the whole
 * of it: a crate that has already been emptied still carries a `giveItemId`, so
 * it can never stop looking like a lever, but opening it a second time changes
 * nothing. By `applyCommand`'s own rule -- the commands that changed the world
 * are the commands that cost a turn -- that second opening is free, exactly as
 * reading a signpost is. Charging for it would mean a player could burn the
 * clock re-opening an empty box, which is the same brute-force hole
 * `16_Puzzle_Charter.md`'s closed-budget rule exists to shut, approached from
 * the other side. */
export function interactChangedTheWorld(outcome: InteractOutcome): boolean {
  switch (outcome.kind) {
    case 'none':
      return false;
    case 'item':
    case 'rune':
    case 'unlocked':
      return !outcome.already;
    case 'placed':
      return !outcome.missing;
    // Always. A breaker has no "already": it went the other way, and the other
    // way is a different circuit.
    case 'breaker':
      return true;
    // A console that reached no belt threw a lever attached to nothing, which is
    // exactly as free as reading a signpost.
    case 'conveyor':
      return outcome.belts > 0;
  }
}
